// CreateBlogPage.tsx
import React, { useEffect, useState } from 'react';
import { fadeTimeout, getOverallPadding } from '../helpers/Themes';
import {
    Button,
    CardActionArea,
    CardContent,
    Fade,
    LinearProgress,
    Stack,
    Step,
    StepContent,
    StepIconProps,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import { useCustomTheme } from '../contexts/ThemeContext';
import { useApi } from '../contexts/ApiContext';
import {
    Blog,
    CreateBlogBasicDetailsProps,
    CreateBlogSectionsProps,
    CreateBlogSectionProps,
    CreateUploadBasicDetailsProps,
    CreateUploadFinalizeProps,
    CreateUploadProps,
    downloadTypeOptions,
    earlyAccessReleaseAddedDays,
    FileUploaderProps,
    Game,
    GamesGrouped,
    standardReleaseAddedDays,
    User,
    CreateBlogProps,
} from '../workers/ApiWorker';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import Box from '@mui/material/Box';
import { Formik, Form, FieldArray, getIn as formikGetIn } from 'formik';
import TextInput from '../components/universal/inputs/TextInput';
import * as Yup from 'yup';
import Card from '@mui/material/Card';
import PageTopSection from '../components/universal/pageTopSection/PageTopSection';
import ImageUpload from '../components/universal/images/ImageUploads';
import FileUpload from '../components/universal/files/FileUploads';
import ErrorAlert from '../components/universal/alerts/ErrorAlert';
import SuccessAlert from '../components/universal/alerts/SuccessAlert';
import ProgressAlert from '../components/universal/alerts/ProgressAlert';
import { v4 as uuidv4 } from 'uuid';
import {
    CREATE_BLOG_FAILED,
    CREATE_BLOG_GET_IMAGE_FAILED,
    CREATE_DIRECT_UPLOAD_CORE_DATA_FAILED,
    CREATE_UPLOAD_FAILED,
    CREATE_UPLOAD_GET_IMAGE_FAILED,
    GENERIC_ERROR,
    SEND_TO_DISCORD_FAILED,
    SEND_TO_DISCORD_SUCCESS,
    UPLOAD_FILE_FAILED,
    UPLOAD_IMAGE_FAILED,
} from '../helpers/Messages';
import { wait } from '@testing-library/user-event/dist/utils';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import BlogPreview from '../components/blog/BlogPreview';
import { isUserStaff } from '../helpers/Users';
import { useNavigate } from 'react-router-dom';
import { BlogCreateLink, PrivacyPolicyLink } from '../helpers/Links';
import SelectInput from '../components/universal/inputs/SelectInput';
import MultiSelectInput from '../components/universal/inputs/MultiSelectInput';
import RichTextInput from '../components/universal/inputs/RichTextInput';
import { CheckCircle, Image, Info, Newspaper } from '@mui/icons-material';
import ReuseDetailsModal from '../components/modal/ReuseDetailsModal';
import { AxiosProgressEvent } from 'axios';
import CheckboxInput from '../components/universal/inputs/CheckboxInput';
import SearchMultiSelectInput from '../components/universal/inputs/SearchMultiSelectInput';
import TagInput from '../components/universal/inputs/TagInput';
import RadioInput from '../components/universal/inputs/RadioInput';
import DateTimeInput from '../components/universal/inputs/DateTimeInput';
import BlogImageUpload from '../components/universal/images/BlogImageUploads';
import { getIn } from 'yup';
import { laravelSlug } from '../helpers/Slug';

interface BlogPageProps {
    isMobile: boolean;
    padding?: number;
    user?: User;
}

interface StepProps {
    label: string;
    info: string;
    icon: JSX.Element;
    content: JSX.Element;
}

const createApiWorker = createWorkerFactory(
    () => import('../workers/ApiWorker')
);

// Validation Schema
const validationSchema = Yup.object({
    name: Yup.string()
        .required('Title is required')
        .max(25, 'Caption must be at most 25 characters'),
    miniDescription: Yup.string()
        .required('Caption is required')
        .max(45, 'Caption must be at most 45 characters'),
});

export const validationSchemaCreateBlog = Yup.object({
    sections: Yup.array()
        .of(
            Yup.object({
                name: Yup.string().trim().required('Section name is required'),
                content: Yup.string().required('Content is required'),
            })
        )
        .min(1, 'At least one section is required'),
});

const CreateBlogPage: React.FC<BlogPageProps> = ({
    isMobile,
    padding,
    user,
}) => {
    const { theme } = useCustomTheme();
    const { token, logout } = useApi();
    const navigate = useNavigate(); // Hook for navigation
    const apiWorker = useWorker(createApiWorker);
    const blogCreateLink = BlogCreateLink(isMobile);

    const [error, setError] = React.useState<string | undefined>(undefined);
    const [success, setSuccess] = React.useState<string | undefined>(undefined);

    const [uuid, setUuid] = useState<string>(uuidv4());
    const [progress, setProgress] = React.useState<number>(0);

    const [uploadImage, setUploadImage] = useState<File | undefined>(undefined);

    const createBlogBasicDetailsDefault: CreateBlogBasicDetailsProps = {
        name: '',
        miniDescription: '',
    };

    const createBlogSectionsDetailsDefault: CreateBlogSectionsProps = {
        sections: [
            {
                name: '',
                content: '',
            },
        ] as CreateBlogSectionProps[],
    };

    const [createBlogBasicDetails, setCreateBlogBasicDetails] =
        React.useState<CreateBlogBasicDetailsProps>(
            createBlogBasicDetailsDefault
        );

    const [createBlogSectionsDetails, setCreateBlogSectionsDetails] =
        React.useState<CreateBlogSectionsProps>(
            createBlogSectionsDetailsDefault
        );

    const [allDataSubmitted, setAllDataSubmitted] = useState(false);

    const handleImageUpload = async (value?: File) => {
        setUploadImage(value);
    };

    // Custom StepIcon component to create the blue circle with the white icon
    const CustomStepIcon: React.FC<
        StepIconProps & { iconElement: JSX.Element }
    > = ({ iconElement, active, completed, className }) => {
        return (
            <Box
                className={className} // This ensures default MUI styling (active/completed states)
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 34, // Circle width
                    height: 34, // Circle height
                    borderRadius: '50%', // Ensures the icon is circular
                    backgroundColor:
                        active || completed
                            ? theme.palette.primary.main
                            : theme.palette.primary.main,
                    position: 'relative',
                    marginLeft: -0.55, // Adjust margin to align with the step line
                }}
            >
                {iconElement}
            </Box>
        );
    };

    const [activeStep, setActiveStep] = React.useState(0);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = (values?: CreateBlogSectionsProps) => {
        if (activeStep === 2) {
            if (values) {
                console.log(values);
                setCreateBlogSectionsDetails(values);
            }
        }
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const steps: StepProps[] = [
        {
            label: 'BASIC DETAILS',
            info: 'Essential information required for your blog post',
            icon: <Info />,
            content: (
                <>
                    {user && token ? (
                        <Formik
                            enableReinitialize
                            initialValues={createBlogBasicDetails}
                            validationSchema={validationSchema}
                            onSubmit={async (values, { resetForm }) => {
                                setError('');
                                const rawName = values.name;
                                const slug = laravelSlug(rawName);

                                try {
                                    await apiWorker.getBlog(slug);

                                    setError('This title is already in use');
                                } catch (e) {
                                    setCreateBlogBasicDetails(values);
                                    handleNext();
                                }
                            }}
                        >
                            {({
                                errors,
                                touched,
                                values,
                                handleChange,
                                isSubmitting,
                                handleBlur,
                                isValid,
                                setFieldValue,
                            }) => {
                                return (
                                    <Form>
                                        <TextInput
                                            id={'name'}
                                            label={'TITLE *'}
                                            value={values.name}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            hasSubmitted={isSubmitting}
                                            touched={touched.name}
                                            errors={errors.name}
                                            isMobile={isMobile}
                                        />
                                        <TextInput
                                            id={'miniDescription'}
                                            label={'CAPTION *'}
                                            value={values.miniDescription}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            hasSubmitted={isSubmitting}
                                            touched={touched.miniDescription}
                                            errors={errors.miniDescription}
                                            isMobile={isMobile}
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                marginTop: '1rem',
                                            }}
                                        >
                                            <Button
                                                type="submit"
                                                disabled={
                                                    isSubmitting || !isValid
                                                }
                                                variant="contained"
                                                sx={{
                                                    padding: '0.8rem',
                                                }}
                                            >
                                                CONTINUE
                                            </Button>
                                        </Box>
                                    </Form>
                                );
                            }}
                        </Formik>
                    ) : null}
                </>
            ),
        },
        {
            label: 'MEDIA',
            info: 'Main visual content for your blog post. Accepted formats include JPEG, PNG and WEBP',
            icon: <Image />,
            content: (
                <>
                    <BlogImageUpload
                        uploadData={{
                            title: createBlogBasicDetails.name,
                            description: createBlogBasicDetails.miniDescription,
                        }}
                        file={uploadImage}
                        handleImageUpload={handleImageUpload}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '1rem',
                        }}
                    >
                        <Button
                            type="button"
                            disabled={!uploadImage}
                            variant="contained"
                            onClick={() => handleNext()}
                            sx={{
                                padding: '0.8rem',
                                marginLeft: 2,
                            }}
                        >
                            CONTINUE
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            color="inherit"
                            sx={{
                                padding: '0.8rem',
                                marginLeft: 2,
                            }}
                            onClick={() => handleBack()}
                        >
                            BACK
                        </Button>
                    </Box>
                </>
            ),
        },
        {
            label: 'SECTIONS',
            info: 'Configure the sections for the blog post',
            icon: <Newspaper />,
            content: (
                <>
                    {user ? (
                        <Formik
                            enableReinitialize
                            initialValues={createBlogSectionsDetails}
                            validationSchema={validationSchemaCreateBlog}
                            onSubmit={async (values, { resetForm }) => {
                                setProgress(0);
                                setAllDataSubmitted(true);
                                setSuccess('');
                                setError('');

                                if (token) {
                                    if (uploadImage) {
                                        let createBlog = undefined;
                                        let createImage = undefined;

                                        try {
                                            createImage =
                                                await apiWorker.postImage(
                                                    {
                                                        uuid: uuid,
                                                        type: 'main',
                                                        scope: 'blogs',
                                                        image: uploadImage,
                                                    },
                                                    token,
                                                    (
                                                        progressEvent: AxiosProgressEvent
                                                    ) => {
                                                        const percentCompleted =
                                                            Math.round(
                                                                (progressEvent.loaded *
                                                                    100) /
                                                                    (progressEvent.total ??
                                                                        progressEvent.loaded)
                                                            );
                                                        setProgress(
                                                            percentCompleted
                                                        );
                                                    }
                                                );
                                        } catch (error) {
                                            setError(UPLOAD_IMAGE_FAILED);
                                        }

                                        if (createImage) {
                                            try {
                                                const blogData: CreateBlogProps =
                                                    {
                                                        ...values,
                                                        ...createBlogBasicDetails,
                                                    };

                                                createBlog =
                                                    await apiWorker.postBlog(
                                                        blogData,
                                                        token,
                                                        uuid,
                                                        createImage.data.key
                                                    );

                                                navigate(
                                                    `/blog/${createBlog.data.slug}`
                                                );
                                            } catch (error) {
                                                setError(CREATE_BLOG_FAILED);
                                            }
                                        }
                                    } else {
                                        setError(CREATE_BLOG_GET_IMAGE_FAILED);
                                    }

                                    setAllDataSubmitted(false);
                                    setProgress(0);
                                } else {
                                    logout('/login');
                                }
                            }}
                        >
                            {({
                                errors,
                                touched,
                                values,
                                handleChange,
                                isSubmitting,
                                handleBlur,
                                isValid,
                                setFieldValue,
                            }) => (
                                <Form>
                                    {/* Sections editor */}
                                    <FieldArray name="sections">
                                        {({ push, remove }) => (
                                            <>
                                                {values.sections.map(
                                                    (section, idx: number) => {
                                                        const namePath = `sections.${idx}.name`;
                                                        const contentPath = `sections.${idx}.content`;

                                                        const nameTouched =
                                                            !!formikGetIn(
                                                                touched,
                                                                namePath
                                                            );
                                                        const nameError =
                                                            formikGetIn(
                                                                errors,
                                                                namePath
                                                            ) as
                                                                | string
                                                                | undefined;

                                                        const contentTouched =
                                                            !!formikGetIn(
                                                                touched,
                                                                contentPath
                                                            );
                                                        const contentError =
                                                            formikGetIn(
                                                                errors,
                                                                contentPath
                                                            ) as
                                                                | string
                                                                | undefined;

                                                        const isLast =
                                                            idx ===
                                                            values.sections
                                                                .length -
                                                                1;

                                                        return (
                                                            <Box
                                                                key={idx}
                                                                sx={{ mb: 3 }}
                                                            >
                                                                {/* Section Name */}
                                                                <TextInput
                                                                    id={
                                                                        namePath
                                                                    }
                                                                    label={
                                                                        'SECTION NAME *'
                                                                    }
                                                                    value={
                                                                        section.name
                                                                    }
                                                                    handleChange={
                                                                        handleChange
                                                                    }
                                                                    handleBlur={
                                                                        handleBlur
                                                                    }
                                                                    hasSubmitted={
                                                                        allDataSubmitted
                                                                    }
                                                                    touched={
                                                                        nameTouched
                                                                    }
                                                                    errors={
                                                                        nameError
                                                                    }
                                                                    isMobile={
                                                                        isMobile
                                                                    }
                                                                />

                                                                {/* Section Content */}
                                                                <RichTextInput
                                                                    images={true}
                                                                    id={
                                                                        contentPath
                                                                    }
                                                                    label={
                                                                        'CONTENT *'
                                                                    }
                                                                    value={
                                                                        section.content
                                                                    }
                                                                    errors={
                                                                        contentError
                                                                    }
                                                                    handleBlur={
                                                                        handleBlur
                                                                    }
                                                                    hasSubmitted={
                                                                        allDataSubmitted
                                                                    }
                                                                    touchedFormik={
                                                                        contentTouched
                                                                    }
                                                                    handleChange={
                                                                        setFieldValue
                                                                    }
                                                                    isMobile={
                                                                        isMobile
                                                                    }
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        gap: 1,
                                                                        mt: 1,
                                                                    }}
                                                                >
                                                                    {/* Remove on all rows (disable when only one section) */}
                                                                    <Button
                                                                        type="button"
                                                                        variant="outlined"
                                                                        color="error"
                                                                        onClick={() =>
                                                                            remove(
                                                                                idx
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            allDataSubmitted ||
                                                                            values
                                                                                .sections
                                                                                .length ===
                                                                                1
                                                                        }
                                                                    >
                                                                        Remove
                                                                    </Button>

                                                                    {/* Add only on the last row */}
                                                                    {isLast && (
                                                                        <Button
                                                                            disabled={
                                                                                allDataSubmitted
                                                                            }
                                                                            type="button"
                                                                            variant="outlined"
                                                                            color="warning"
                                                                            onClick={() =>
                                                                                push(
                                                                                    {
                                                                                        name: '',
                                                                                        content:
                                                                                            '',
                                                                                    }
                                                                                )
                                                                            }
                                                                        >
                                                                            Add
                                                                            Section
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    }
                                                )}
                                            </>
                                        )}
                                    </FieldArray>

                                    {/* Actions row */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            mt: 3,
                                        }}
                                    >
                                        <Button
                                            type="submit"
                                            disabled={isSubmitting || !isValid}
                                            variant="contained"
                                            sx={{ padding: '0.8rem' }}
                                        >
                                            SAVE DRAFT & PREVIEW
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            color="inherit"
                                            sx={{ padding: '0.8rem', ml: 2 }}
                                            onClick={() => handleBack(values)}
                                        >
                                            BACK
                                        </Button>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    ) : null}
                </>
            ),
        },
    ];

    const topSectionInfo = (
        <>Please enter the details you would like to add to your blog post</>
    );

    return (
        <Fade in={true} timeout={fadeTimeout}>
            <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <PageTopSection
                    isMobile={isMobile}
                    padding={padding}
                    simpleContent={{
                        title: 'BLOG',
                        info: topSectionInfo,
                    }}
                />
                <Box
                    sx={{
                        paddingLeft: getOverallPadding(isMobile, padding),
                        paddingRight: getOverallPadding(isMobile, padding),
                        marginBottom: isMobile ? 10 : 3,
                    }}
                >
                    {error && <ErrorAlert message={error}></ErrorAlert>}

                    {success && <SuccessAlert message={success}></SuccessAlert>}

                    {allDataSubmitted && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                marginBottom: 2,
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {`${Math.round(progress)}%`}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Fade in={true} timeout={fadeTimeout}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((step, index) => (
                                <Step key={step.label}>
                                    <Fade in={true} timeout={fadeTimeout}>
                                        <div>
                                            <StepLabel
                                                StepIconComponent={(props) => (
                                                    <CustomStepIcon
                                                        {...props}
                                                        iconElement={step.icon}
                                                    />
                                                )}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: isMobile
                                                            ? 'column'
                                                            : 'row', // Change direction based on mobile view
                                                        alignItems: isMobile
                                                            ? 'flex-start'
                                                            : 'center', // Align items differently on mobile
                                                        paddingLeft: !isMobile
                                                            ? 2
                                                            : 0,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            color: theme.palette
                                                                .text.primary,
                                                            cursor: 'default',
                                                        }}
                                                    >
                                                        {step.label}
                                                    </Typography>
                                                    {/* Add step.info below the label in mobile view */}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            marginLeft: isMobile
                                                                ? 0.1
                                                                : 1, // No left margin in mobile view
                                                            color: theme.palette
                                                                .text.secondary, // Keep the secondary color
                                                            cursor: 'default',
                                                        }}
                                                    >
                                                        {step.info}
                                                    </Typography>
                                                </Box>
                                            </StepLabel>

                                            <StepContent
                                                sx={{
                                                    paddingLeft: !isMobile
                                                        ? 5
                                                        : 3.2,
                                                }}
                                            >
                                                <Card>
                                                    <CardContent>
                                                        {step.content}
                                                    </CardContent>
                                                </Card>
                                            </StepContent>
                                        </div>
                                    </Fade>
                                </Step>
                            ))}
                        </Stepper>
                    </Fade>
                </Box>
            </Box>
        </Fade>
    );
};

export default CreateBlogPage;
